// The cut order of an episode, from GET /episodes/{id}/structure: every shot in the order of the cut,
// whatever scene (or the hidden default scene) it sits in. The default scene is flagged `is_default` and the
// studio never shows it as a scene; this file only reads the order.
import type { Api } from "./api.ts";

export interface EpisodeStructure {
  episode_id: string;
  has_named_scenes: boolean;
  scenes: {
    id: string;
    code: string;
    slugline: string | null;
    position: number;
    is_default: boolean;
    shots: { shot_id: string; code: string; placement_id: string; position: number }[];
  }[];
  shots: { shot_id: string; code: string; scene_id: string; placement_id: string }[];
}

export const fetchStructure = (api: Api, ws: string, episodeId: string) =>
  api.request<EpisodeStructure>("GET", `/workspaces/${ws}/episodes/${episodeId}/structure`);

/** shot id → position in the cut (0 is first). */
export function cutOrder(s: Pick<EpisodeStructure, "shots"> | null | undefined): Map<string, number> {
  return new Map((s?.shots ?? []).map((x, i) => [x.shot_id, i]));
}

/** Sort shots by the cut order; a stable sort, so shots the structure does not know keep their order, at the end. */
export function inCutOrder<T extends { id: string }>(
  shots: readonly T[],
  order: ReadonlyMap<string, number> | undefined,
): T[] {
  if (!order || order.size === 0) return [...shots];
  const rank = (s: T) => order.get(s.id) ?? Number.MAX_SAFE_INTEGER;
  return [...shots].sort((a, b) => rank(a) - rank(b));
}
